/**
 * Planner collision logic (CONTRACTS.md §4, master spec §8.3).
 *
 * Canonical interval rule:
 *   newStart < existingEnd AND newEnd > existingStart
 *
 * Anything weaker (e.g. comparing identical quarter positions) lets a long
 * item swallow shorter items that start inside its interval — a verified
 * defect of the previous build.
 */
import type { PlannerItem } from '@/types'

export const MINUTES_PER_DAY = 24 * 60
export const QUARTER_MINUTES = 15

/** Two half-open minute intervals [start, end) overlap. */
export function intervalsOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aStart < bEnd && aEnd > bStart
}

/**
 * True when a new block of `durationMinutes` starting at `startMinute`
 * overlaps any existing item. Touching boundaries (new start exactly at an
 * existing end, or new end exactly at an existing start) are allowed.
 * Pass `ignoreId` when editing an item so it doesn't collide with itself.
 */
export function hasCollision(
  items: PlannerItem[],
  startMinute: number,
  durationMinutes: number,
  ignoreId?: string,
): boolean {
  const newEnd = startMinute + durationMinutes
  return items.some((item) => {
    if (ignoreId !== undefined && item.id === ignoreId) return false
    return intervalsOverlap(startMinute, newEnd, item.startMinute, item.startMinute + item.durationMinutes)
  })
}

export type FindNextFreeSlotOptions = {
  /** Earliest candidate minute (default 0). */
  fromMinute?: number
  /** Latest allowed minute for the block start (default 24:00 - duration). */
  untilMinute?: number
  /** Search step in minutes (default quarter-hour placement grid). */
  stepMinutes?: number
  /** Item to ignore (edit flows). */
  ignoreId?: string
}

/**
 * Finds the first start minute at or after `fromMinute` (on the placement
 * grid) where the block fits without colliding. Returns null when no free
 * slot exists inside the window.
 */
export function findNextFreeSlot(
  items: PlannerItem[],
  durationMinutes: number,
  options: FindNextFreeSlotOptions = {},
): number | null {
  const step = options.stepMinutes ?? QUARTER_MINUTES
  const until = options.untilMinute ?? MINUTES_PER_DAY - durationMinutes
  let candidate = options.fromMinute ?? 0
  if (candidate < 0) candidate = 0
  // Snap the first candidate up to the grid.
  candidate = Math.ceil(candidate / step) * step

  const relevant = items
    .filter((item) => item.id !== options.ignoreId)
    .map((item) => ({ start: item.startMinute, end: item.startMinute + item.durationMinutes }))
    .sort((a, b) => a.start - b.start)

  while (candidate <= until) {
    const end = candidate + durationMinutes
    const blocker = relevant.find((item) => intervalsOverlap(candidate, end, item.start, item.end))
    if (!blocker) return candidate
    // Jump to the blocker's end (snapped up to the grid) instead of stepping
    // one quarter at a time through a long occupied interval.
    candidate = Math.ceil(blocker.end / step) * step
  }
  return null
}

/** Snaps a minute-of-day to the nearest quarter-hour, clamped to the day. */
export function snapToQuarter(minute: number): number {
  const snapped = Math.round(minute / QUARTER_MINUTES) * QUARTER_MINUTES
  return Math.min(Math.max(snapped, 0), MINUTES_PER_DAY - QUARTER_MINUTES)
}
